import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ZoomIn, ZoomOut, RotateCw, Download, ExternalLink } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

const TEXT_PREVIEW_MAX_BYTES = 1024 * 1024;

interface TextPreview {
  content: string;
  isTruncated: boolean;
}

async function readTextPreview(response: Response): Promise<TextPreview> {
  const reader = response.body?.getReader();
  if (!reader) {
    return { content: "", isTruncated: false };
  }

  const decoder = new TextDecoder();
  let content = "";
  let remainingBytes = TEXT_PREVIEW_MAX_BYTES;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      return { content: content + decoder.decode(), isTruncated: false };
    }

    if (value.byteLength > remainingBytes) {
      content += decoder.decode(value.subarray(0, remainingBytes), { stream: true });
      content += decoder.decode();
      await reader.cancel();
      return { content, isTruncated: true };
    }

    content += decoder.decode(value, { stream: true });
    remainingBytes -= value.byteLength;
  }
}

if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = `${window.location.origin}/pdf.worker.min.mjs?v=${pdfjs.version}-legacy`;
}

interface DocumentViewerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filename?: string;
  mimeType?: string | null;
  blobUrl: string | null;
  isLoading: boolean;
  error: string | null;
  onDownload: () => void;
  onOpenInNewTab: () => void;
}

export function DocumentViewerModal({
  open,
  onOpenChange,
  filename,
  mimeType,
  blobUrl,
  isLoading,
  error,
  onDownload,
  onOpenInNewTab,
}: DocumentViewerModalProps) {
  const DEFAULT_SCALE = 0.85;
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [rotation, setRotation] = useState(0);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<TextPreview | null>(null);
  const [textError, setTextError] = useState<string | null>(null);

  const isPdf = useMemo(() => {
    if (mimeType === "application/pdf") return true;
    return filename?.toLowerCase().endsWith(".pdf") ?? false;
  }, [filename, mimeType]);

  const isText = useMemo(() => {
    if (isPdf) return false;
    if (mimeType === "text/plain") return true;
    return filename?.toLowerCase().endsWith(".txt") ?? false;
  }, [filename, mimeType, isPdf]);

  useEffect(() => {
    if (!open) return;
    setNumPages(0);
    setScale(DEFAULT_SCALE);
    setRotation(0);
    setRenderError(null);
  }, [open, blobUrl]);

  useEffect(() => {
    if (!open || !isText || !blobUrl) {
      setTextContent(null);
      setTextError(null);
      return;
    }
    let cancelled = false;
    setTextContent(null);
    setTextError(null);
    fetch(blobUrl)
      .then(readTextPreview)
      .then((preview) => {
        if (!cancelled) setTextContent(preview);
      })
      .catch(() => {
        if (!cancelled) setTextError("Failed to load text preview.");
      });
    return () => {
      cancelled = true;
    };
  }, [open, isText, blobUrl]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[92vw] h-[92vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-4 py-3 border-b">
          <DialogTitle className="truncate text-sm" title={filename ?? "Document preview"}>
            {filename ?? "Document preview"}
          </DialogTitle>
        </DialogHeader>

        {isPdf && !isLoading && !error && !renderError && (
          <div className="px-3 py-2 border-b bg-muted/40 flex items-center justify-between gap-2">
            <span className="text-xs min-w-[74px]">Pages: {numPages || 0}</span>

            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={scale <= 0.5}
                onClick={() => setScale((s) => Math.max(0.5, Number((s - 0.1).toFixed(1))))}
                title="Zoom out"
              >
                <ZoomOut className="h-3.5 w-3.5" />
              </Button>
              <span className="text-xs min-w-[52px] text-center">{Math.round(scale * 100)}%</span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={scale >= 2.5}
                onClick={() => setScale((s) => Math.min(2.5, Number((s + 0.1).toFixed(1))))}
                title="Zoom in"
              >
                <ZoomIn className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={() => setRotation((r) => (r + 90) % 360)}
                title="Rotate"
              >
                <RotateCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto bg-muted/30 p-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading document preview...
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : !blobUrl ? (
            <p className="text-sm text-muted-foreground">No preview available.</p>
          ) : isText ? (
            textError ? (
              <p className="text-sm text-destructive">{textError}</p>
            ) : textContent === null ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading text preview...
              </div>
            ) : (
              <>
                {textContent.isTruncated && (
                  <p className="mb-2 text-xs text-muted-foreground">
                    Preview truncated to 1 MiB. Download the file to view the complete text.
                  </p>
                )}
                <pre className="whitespace-pre-wrap break-words text-xs font-mono bg-background rounded-md border p-4">
                  {textContent.content}
                </pre>
              </>
            )
          ) : !isPdf ? (
            <div className="text-center space-y-1">
              <p className="text-sm">Preview is currently supported for PDF and text documents only.</p>
              <p className="text-xs text-muted-foreground">Use Download or Open in New Tab for this file type.</p>
            </div>
          ) : (
            <Document
              file={blobUrl}
              loading={null}
              onLoadSuccess={({ numPages: pages }) => {
                setNumPages(pages);
                setRenderError(null);
              }}
              onLoadError={(e) => {
                setRenderError(e instanceof Error ? e.message : "Failed to load PDF preview.");
              }}
            >
              {renderError ? (
                <p className="text-sm text-destructive">{renderError}</p>
              ) : (
                <div className="space-y-4 flex flex-col items-center">
                  {Array.from(new Array(numPages), (_, index) => (
                    <Page
                      key={`pdf-page-${index + 1}`}
                      pageNumber={index + 1}
                      scale={scale}
                      rotate={rotation}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  ))}
                </div>
              )}
            </Document>
          )}
        </div>

        <DialogFooter className="px-4 py-3 border-t gap-2">
          <Button type="button" variant="outline" onClick={onOpenInNewTab} disabled={!blobUrl || isLoading}>
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Open in New Tab
          </Button>
          <Button type="button" onClick={onDownload} disabled={isLoading}>
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
